/**
 * Case display helpers — translate raw DB values to humane UI strings.
 *
 * Per the design spec: "Raw database values must never show in UI (e.g.,
 * 'class_ii' must display as 'Class II')". And: "All empty fields show
 * 'Not specified' — never a bare dash."
 */
import {
    CheckCircle, Clock, Factory, FlagCheckered, Hammer, Truck,
    UserCircle, Warning, FileX, CurrencyInr, Calendar, MagnifyingGlass,
} from "@phosphor-icons/react";

export const EMPTY = "Not specified";

/* ─────────────────────────────────────────────────────────────────── */
/* Formatters                                                          */
/* ─────────────────────────────────────────────────────────────────── */
export function fmt(value) {
    if (value === null || value === undefined) return EMPTY;
    const s = String(value).trim();
    return s ? s : EMPTY;
}

/** "class_ii" → "Class II"; "rush" → "Rush"; "do_not_touch" → "Do Not Touch" */
export function humanize(value) {
    if (value === null || value === undefined) return EMPTY;
    const s = String(value).trim();
    if (!s) return EMPTY;
    return s
        .split(/[_\s-]+/)
        .map((w) => {
            // Roman-numeral suffixes (II, III, IV) stay upper-case
            if (/^i+$/i.test(w) || /^iv$/i.test(w) || /^v$/i.test(w)) return w.toUpperCase();
            return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
        })
        .join(" ");
}

export function fmtRupees(n) {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "₹0";
    const num = Number(n);
    return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export function fmtDate(iso) {
    if (!iso) return EMPTY;
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
        });
    } catch { return EMPTY; }
}

export function fmtDateTime(iso) {
    if (!iso) return EMPTY;
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return EMPTY; }
}

export function fmtAgo(iso) {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diff)) return "";
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
}

/* ─────────────────────────────────────────────────────────────────── */
/* Status badge colors + 7-stage pathway mapping                       */
/* ─────────────────────────────────────────────────────────────────── */
export const STATUS_LABELS = {
    draft: "Draft",
    submitted: "Submitted",
    in_planning: "In Planning",
    submitted_to_qa: "Internal QA",
    sent_for_approval: "Awaiting Doctor",
    revision_requested: "Revision",
    approved: "Doctor Approved",
    in_production: "In Production",
    shipped: "Shipped",
    in_treatment: "In Treatment",
    treatment_started: "Treatment Started",
    refinement_requested: "Refinement",
    completed: "Completed",
    cancelled: "Cancelled",
};

/** Color tokens for the status badge — used by Header + various row pills. */
export function statusTone(status) {
    if (["approved"].includes(status)) return "emerald";
    if (["in_production"].includes(status)) return "amber";
    if (["sent_for_approval", "submitted_to_qa"].includes(status)) return "violet";
    if (["shipped", "in_treatment", "treatment_started"].includes(status)) return "sky";
    if (["completed"].includes(status)) return "emerald";
    if (["cancelled", "revision_requested"].includes(status)) return "rose";
    return "slate";
}

export const PRIORITY_TONES = {
    normal: { bg: "bg-slate-100", text: "text-slate-700", label: "Normal" },
    urgent: { bg: "bg-amber-100", text: "text-amber-800", label: "Urgent" },
    rush:   { bg: "bg-rose-100",  text: "text-rose-700",  label: "Rush" },
};

/* ─────────────────────────────────────────────────────────────────── */
/* 7-stage Clinical Pathway                                            */
/* ─────────────────────────────────────────────────────────────────── */
/** Map a case.status to a stage index 0..6 (or -1 if cancelled). */
export const PATHWAY = [
    { key: "submission",   label: "Case Submission",   Icon: FileX },
    { key: "admin_review", label: "Admin Review",      Icon: UserCircle },
    { key: "planning",     label: "Treatment Planning",Icon: Hammer },
    { key: "doctor_appr",  label: "Doctor Approval",   Icon: CheckCircle },
    { key: "production",   label: "Production",        Icon: Factory },
    { key: "qc",           label: "QC & Packaging",    Icon: MagnifyingGlass },
    { key: "shipped",      label: "Shipped",           Icon: Truck },
];

export function stageIndexForStatus(status) {
    switch (status) {
        case "draft": return 0;
        case "submitted": return 1;
        case "in_planning":
        case "submitted_to_qa":
        case "sent_for_approval":
        case "revision_requested":
            return 2;
        case "approved":
            return 3;
        case "in_production":
            return 4;
        case "shipped":
        case "in_treatment":
        case "treatment_started":
            return 6;
        case "completed":
            return 6;
        case "cancelled":
            return -1;
        default:
            return 0;
    }
}

/* ─────────────────────────────────────────────────────────────────── */
/* Note category tones                                                 */
/* ─────────────────────────────────────────────────────────────────── */
export const NOTE_CATEGORY_TONES = {
    general:    { bg: "bg-slate-100",  text: "text-slate-700",   label: "General" },
    urgent:     { bg: "bg-rose-100",   text: "text-rose-700",    label: "Urgent" },
    qc:         { bg: "bg-amber-100",  text: "text-amber-800",   label: "QC" },
    production: { bg: "bg-sky-100",    text: "text-sky-700",     label: "Production" },
    finance:    { bg: "bg-emerald-100",text: "text-emerald-700", label: "Finance" },
};

/* ─────────────────────────────────────────────────────────────────── */
/* Production checklist + QC checklist labels (display only)           */
/* ─────────────────────────────────────────────────────────────────── */
export const PRODUCTION_STEP_LABELS = {
    stl_files_received: "STL files received",
    files_verified: "Files verified",
    material_pulled: "Material pulled from inventory",
    print_started: "Print job started",
    print_completed: "Print completed",
    trim_and_finish: "Trimming & finishing",
    ready_for_qc: "Ready for QC",
};

export const QC_TOGGLE_LABELS = {
    aligner_fit: "Aligner fit check",
    trim_line: "Trim line accuracy",
    surface_finish: "Surface finish",
    attachment_accuracy: "Attachment accuracy",
    label_packaging: "Label & packaging check",
};

/* ─────────────────────────────────────────────────────────────────── */
/* Clinical enum labels — display-friendly per spec                    */
/*   "minimise_ipr" → "Minimise" (NOT "Minimise Ipr")                  */
/*   "class_ii"     → "Class II"                                       */
/* ─────────────────────────────────────────────────────────────────── */
const CLINICAL_LABELS = {
    // IPR preferences
    minimise_ipr: "Minimise",
    aggressive_ipr: "Aggressive",
    as_needed: "As needed",
    no_ipr: "No IPR",
    standard_ipr: "Standard",
    // Attachment preferences
    minimise: "Minimise",
    aggressive: "Aggressive",
    standard: "Standard",
    no_attachments: "No attachments",
    // Elastics
    none: "None",
    class_i: "Class I",
    class_ii: "Class II",
    class_iii: "Class III",
    cross_bite: "Cross-bite",
    // Extractions
    no_extractions: "No extractions",
    extraction_planned: "Extraction planned",
    as_planned: "As planned per chart",
    // Arches
    both: "Both arches",
    upper: "Upper arch only",
    lower: "Lower arch only",
    // Genders
    male: "Male",
    female: "Female",
    other: "Other",
    // Retainer types
    clear_vivera_style: "Clear (Vivera-style)",
    wire_essix: "Wire / Essix hybrid",
    hawley: "Hawley",
    fixed_lingual: "Fixed lingual bonded",
};

/** Looks up a specific display label first; falls back to humanize. */
export function prettyValue(value) {
    if (value === null || value === undefined) return EMPTY;
    const s = String(value).trim().toLowerCase();
    if (!s) return EMPTY;
    if (CLINICAL_LABELS[s]) return CLINICAL_LABELS[s];
    return humanize(value);
}

/** Field-aware version for ambiguous enums (e.g. attachment_pref vs ipr_pref). */
const FIELD_LABELS = {
    attachment_pref: {
        minimise: "Minimise usage",
        aggressive: "Aggressive usage",
        standard: "Standard",
        no_attachments: "No attachments",
    },
    ipr_pref: {
        minimise_ipr: "Minimise",
        aggressive_ipr: "Aggressive",
        no_ipr: "No IPR",
        as_needed: "As needed",
    },
};
export function prettyField(field, value) {
    if (value === null || value === undefined) return EMPTY;
    const s = String(value).trim().toLowerCase();
    if (!s) return EMPTY;
    if (FIELD_LABELS[field]?.[s]) return FIELD_LABELS[field][s];
    return prettyValue(value);
}
